import { paths } from '@/api/openapi/schema';
import { CANVAS_SIZE, FONT_FAMILIES } from '@/constant';
import { SlideComponentMCCreator } from '@/providers/editor/creators/SlideComponentMC.creator';
import { SlideComponentTextCreator } from '@/providers/editor/creators/SlideComponentText.creator';
import { SlideComponent, SlideComponentMC, SlideComponentMCAnswer, SlideComponentText } from '@/types/slide';

const TRADITIONAL_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZHKXinBiaoKai')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;
const SIMPLIFIED_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZKai-Z03S')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;

// Union type for all possible RC endpoint responses
export type RCResponse =
    | paths['/experiments/generators/questions/igcse/rc/narrative']['post']['responses']['200']['content']['application/json']
    | paths['/experiments/generators/questions/igcse/rc/letter']['post']['responses']['200']['content']['application/json']
    | paths['/experiments/generators/questions/igcse/rc/review']['post']['responses']['200']['content']['application/json'];

function getPassageTextContent(input: RCResponse): string {
    if (!input || input.question?.type !== 'mc' || !input.question.content) {
        return '';
    }
    if (!input || !input.question?.content) {
        return '';
    }

    if (input.question.content.article_type === 'review' && input.question.content.panels && input.question.content.panels.length > 0) {
        // make sure pannel length is 4
        if (input.question.content.panels.length === 4) {
            // make sure all labels A, B, C, D exist
            if (
                input.question.content.panels.some((item) => item.label === 'A') &&
                input.question.content.panels.some((item) => item.label === 'B') &&
                input.question.content.panels.some((item) => item.label === 'C') &&
                input.question.content.panels.some((item) => item.label === 'D')
            ) {
                // all labels exist
                const optionA = input.question.content.panels.find((panel) => panel.label === 'A');
                const optionASentence = optionA ? `A: ${optionA.content}\n` : '';
                const optionB = input.question.content.panels.find((panel) => panel.label === 'B');
                const optionBSentence = optionB ? `B: ${optionB.content}\n` : '';
                const optionC = input.question.content.panels.find((panel) => panel.label === 'C');
                const optionCSentence = optionC ? `C: ${optionC.content}\n` : '';
                const optionD = input.question.content.panels.find((panel) => panel.label === 'D');
                const optionDSentence = optionD ? `D: ${optionD.content}\n` : '';
                return `${optionASentence}${optionBSentence}${optionCSentence}${optionDSentence}`;
            } else {
                console.log('Invalid panels format or missing labels:', input.question.content.panels);
                return '';
            }
        } else {
            console.log('Invalid panels length:', input.question.content.panels.length);
        }
    } else if (input.question.content.article_type === 'narrative' || input.question.content.article_type === 'letter') {
        return input.question.content.passage || '';
    }

    return '';
}

const INSTRUCTION_ZHHK = '請閱讀以下短文，選擇唯一正確的答案：';
const INSTRUCTION_ZHCN = '请阅读以下短文，选择唯一正确的答案：';

export const generate = (input?: RCResponse): SlideComponent[][] => {
    // useTranslation hook cannot be used here, so hardcode the instruction text for now
    if (!input || input.question?.type !== 'mc' || !input.question.content) {
        return [];
    }

    const slides: SlideComponent[][] = [];

    for (const question of input.question.content.questions) {
        const components: SlideComponent[] = [];

        const margin = 16;

        /**
         * Create the instruction text component
         */

        const instruction: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        instruction.content.text = input.question.content.language_variant === 'traditional' ? INSTRUCTION_ZHHK : INSTRUCTION_ZHCN;
        instruction.pos = { x: margin, y: margin };
        instruction.size = { width: CANVAS_SIZE.width - margin * 2, height: 50 };
        instruction.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        instruction.content.fontSize = 16;
        instruction.content.typeSettings.alignment = 'left';
        instruction.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(instruction);

        /**
         * Create the passage text component
         */
        const passageTextContent = getPassageTextContent(input);
        // TODO: I believe some error handling is needed here in case passageTextContent is empty

        const passage: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        passage.content.text = passageTextContent;
        passage.pos = { x: margin, y: margin + instruction.pos.y + instruction.size.height };
        passage.size = { width: CANVAS_SIZE.width - margin * 2, height: 150 };
        passage.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        passage.content.fontSize = 16;
        passage.content.typeSettings.alignment = 'left';
        passage.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(passage);

        /**
         * Create the SlideComponentMC component
         */

        /**
         * Create the question text component
         */
        const questionText: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        questionText.content.text = question.question_text || '';
        questionText.pos = { x: margin, y: 440 };
        questionText.size = { width: CANVAS_SIZE.width - margin * 2, height: 50 };
        questionText.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        questionText.content.fontSize = 16;
        questionText.content.typeSettings.alignment = 'left';
        questionText.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(questionText);

        const mc: SlideComponentMC = new SlideComponentMCCreator().create(crypto.randomUUID());
        mc.pos = { x: 0, y: 484 };
        mc.size = { width: CANVAS_SIZE.width, height: 284 };
        mc.content.answers = [];

        for (let i = 0; i < question.options.length; i++) {
            const option = question.options[i];
            /**
             * Create the answer text component
             */
            const answer: SlideComponentMCAnswer = {
                id: crypto.randomUUID(),
                type: 'text',
                pos: { x: 0, y: 0 },
                size: { width: 0, height: 0 },
                settings: {
                    backgroundColor: '#DADEE7',
                    isAnswer: question.correct_answer === i,
                },
                content: {
                    text: option || '',
                    jyutping: [],
                    fontSize: 16,
                    typeSettings: {
                        fontFamily: input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY,
                        formatting: [],
                        alignment: 'left',
                        isPhonetic: false,
                        borderRadius: 0,
                        backgroundColor: 'transparent',
                    },
                    color: '#000000',
                },
            };

            mc.content.answers.push(answer);
        }
        components.push(mc);
        slides.push(components);
    }

    return slides;
};
